import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { clearAuthData, getUserInfoSync, API_ENDPOINTS } from '../../config/api'
import apiClient from '../../config/api'
import logo from '../../assets/logo.jpeg'

const TopBar = ({ onToggleSidebar, setActiveScreen }) => {
  const navigate = useNavigate()
  const userInfo = getUserInfoSync()
  const [lowStockAlerts, setLowStockAlerts] = useState([])
  const [loadingAlerts, setLoadingAlerts] = useState(false)

  // Get user display name
  const getUserDisplayName = () => {
    if (userInfo?.employee_name) return userInfo.employee_name
    if (userInfo?.full_name) return userInfo.full_name
    if (userInfo?.first_name && userInfo?.last_name) return `${userInfo.first_name} ${userInfo.last_name}`
    if (userInfo?.username) return userInfo.username
    if (userInfo?.email) return userInfo.email.split('@')[0]
    return 'User'
  }

  const fetchLowStockAlerts = useCallback(async () => {
    try {
      setLoadingAlerts(true)
      const response = await apiClient.get(API_ENDPOINTS.BRANCH_LOW_STOCK)
      const data = Array.isArray(response.data) ? response.data : []
      setLowStockAlerts(data)
    } catch (err) {
      console.error('Error fetching low stock alerts:', err)
      setLowStockAlerts([])
    } finally {
      setLoadingAlerts(false)
    }
  }, [])

  useEffect(() => {
    fetchLowStockAlerts()
    // Refresh alerts every 5 minutes
    const interval = setInterval(fetchLowStockAlerts, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchLowStockAlerts])

  const handleProfileClick = (e) => {
    e.preventDefault()
    if (setActiveScreen) {
      setActiveScreen('profile')
    }
  }

  const handleToggleClick = (e) => {
    e.preventDefault()
    if (onToggleSidebar) {
      onToggleSidebar()
    }
  }

  const handleLogout = (e) => {
    e.preventDefault()
    clearAuthData()
    navigate('/login')
  }

  const handleNotificationClick = (e) => {
    e.preventDefault()
    if (setActiveScreen) {
      setActiveScreen('low-stock-alerts')
    }
  }

  const handleViewAllNotifications = (e) => {
    e.preventDefault()
    if (setActiveScreen) {
      setActiveScreen('low-stock-alerts')
    }
  }

  const getStockStatus = (quantity, reorderLevel) => {
    const qty = parseFloat(quantity) || 0
    const reorder = parseFloat(reorderLevel) || 0
    
    if (qty === 0) {
      return { icon: 'bi-x-circle', color: 'text-danger', text: 'Out of Stock' }
    } else if (qty < reorder) {
      return { icon: 'bi-exclamation-circle', color: 'text-warning', text: 'Low Stock' }
    } else {
      return { icon: 'bi-check-circle', color: 'text-success', text: 'In Stock' }
    }
  }

  const formatNumber = (value) => {
    const parsed = parseFloat(value)
    if (Number.isNaN(parsed)) {
      return '0.00'
    }
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Get top 5 most critical alerts (out of stock first, then low stock)
  const sortedAlerts = [...lowStockAlerts].sort((a, b) => {
    const aQty = parseFloat(a.quantity) || 0
    const bQty = parseFloat(b.quantity) || 0
    const aReorder = parseFloat(a.reorder_level) || 0
    const bReorder = parseFloat(b.reorder_level) || 0
    
    // Out of stock items first
    if (aQty === 0 && bQty > 0) return -1
    if (aQty > 0 && bQty === 0) return 1
    
    // Then by how far below reorder level
    const aDiff = aQty - aReorder
    const bDiff = bQty - bReorder
    return aDiff - bDiff
  })

  const displayAlerts = sortedAlerts.slice(0, 5)
  const alertCount = lowStockAlerts.length

  return (
<header id="header" className="header fixed-top d-flex align-items-center">
  <div className="d-flex align-items-center justify-content-between">
    <a href="index.html" className="logo d-flex align-items-center">
      <img src={logo} alt="Logo" />
      <span className="d-none d-lg-block">Decor Masters</span>
    </a>
    <i className="bi bi-list toggle-sidebar-btn" onClick={handleToggleClick} style={{ cursor: 'pointer' }} />
  </div>{/* End Logo */}
 
  <nav className="header-nav ms-auto">
    <ul className="d-flex align-items-center">
      <li className="nav-item d-block d-lg-none">
        <a className="nav-link nav-icon search-bar-toggle " href="#">
          <i className="bi bi-search" />
        </a>
      </li>{/* End Search Icon*/}
      <li className="nav-item dropdown">
        <a className="nav-link nav-icon" href="#" data-bs-toggle="dropdown">
          <i className="bi bi-bell" />
          {alertCount > 0 && (
            <span className="badge bg-danger badge-number">{alertCount}</span>
          )}
        </a>{/* End Notification Icon */}
        <ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow notifications">
          <li className="dropdown-header">
            {loadingAlerts ? (
              <>Loading alerts...</>
            ) : alertCount > 0 ? (
              <>
                You have {alertCount} low stock alert{alertCount !== 1 ? 's' : ''}
                <a href="#" onClick={handleViewAllNotifications} style={{ cursor: 'pointer' }}>
                  <span className="badge rounded-pill bg-primary p-2 ms-2">View all</span>
                </a>
              </>
            ) : (
              <>No low stock alerts</>
            )}
          </li>
          {!loadingAlerts && displayAlerts.length > 0 && (
            <>
              <li>
                <hr className="dropdown-divider" />
              </li>
              {displayAlerts.map((alert, index) => {
                const status = getStockStatus(alert.quantity, alert.reorder_level)
                return (
                  <React.Fragment key={`${alert.product_id}-${alert.branch_id}`}>
                    <li className="notification-item" onClick={handleNotificationClick} style={{ cursor: 'pointer' }}>
                      <i className={`bi ${status.icon} ${status.color}`} />
                      <div>
                        <h4>{alert.product_name || 'Unknown Product'}</h4>
                        <p>
                          {alert.branch_name || 'Unknown Branch'} - Qty: {formatNumber(alert.quantity)} / Reorder: {formatNumber(alert.reorder_level)}
                        </p>
                        <p className="text-muted small">{status.text}</p>
                      </div>
                    </li>
                    {index < displayAlerts.length - 1 && (
                      <li>
                        <hr className="dropdown-divider" />
                      </li>
                    )}
                  </React.Fragment>
                )
              })}
            </>
          )}
          {!loadingAlerts && displayAlerts.length === 0 && alertCount === 0 && (
            <>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li className="notification-item">
                <i className="bi bi-check-circle text-success" />
                <div>
                  <h4>All Stock Levels Normal</h4>
                  <p>No low stock alerts at this time.</p>
                </div>
              </li>
            </>
          )}
          {!loadingAlerts && alertCount > 0 && (
            <>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li className="dropdown-footer">
                <a href="#" onClick={handleViewAllNotifications} style={{ cursor: 'pointer' }}>
                  Show all notifications
                </a>
              </li>
            </>
          )}
        </ul>{/* End Notification Dropdown Items */}
      </li>{/* End Notification Nav */}
      
      <li className="nav-item dropdown pe-3">
        <a className="nav-link nav-profile d-flex align-items-center pe-0" href="#" data-bs-toggle="dropdown">
          <img src="assets/img/profile-img.jpg" alt="Profile" className="rounded-circle" />
          <span className="d-none d-md-block dropdown-toggle ps-2">{getUserDisplayName()}</span>
        </a>{/* End Profile Iamge Icon */}
        <ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow profile">
          <li className="dropdown-header">
            <h6>{getUserDisplayName()}</h6>
            <span>{userInfo?.role_display || 'User'}</span>
          </li>
          <li>
            <hr className="dropdown-divider" />
          </li>
          <li>
            <a className="dropdown-item d-flex align-items-center" href="#" onClick={handleProfileClick} style={{ cursor: 'pointer' }}>
              <i className="bi bi-person" />
              <span>My Profile</span>
            </a>
          </li>
          <li>
            <hr className="dropdown-divider" />
          </li>
          <li>
            <a className="dropdown-item d-flex align-items-center" href="#" onClick={handleLogout} style={{ cursor: 'pointer' }}>
              <i className="bi bi-box-arrow-right" />
              <span>Sign Out</span>
            </a>
          </li>
        </ul>{/* End Profile Dropdown Items */}
      </li>{/* End Profile Nav */}
    </ul>
  </nav>{/* End Icons Navigation */}
</header>

  )
}

export default TopBar